import { createTheme } from '@mui/material/styles';
import { authTokens, emberGradient, FONT_BODY, FONT_DISPLAY, FONT_MONO } from './components/Auth/brandTokens';

export type AppColorMode = 'light' | 'dark';

/**
 * App theme derived from the "Dragonsframe Auth" design system (Claude Design handoff):
 * ink + ember + amber, Bricolage display / Hanken body / Space Mono labels, crisp corners,
 * hairline borders, and an ember-gradient primary CTA. Light + dark. The top app bar is an
 * always-dark "masthead" in both modes (matches the auth masthead and keeps nav legible).
 */
export function createAppTheme(mode: AppColorMode) {
  const isDark = mode === 'dark';
  const t = authTokens(mode);
  const CREAM = '#F5ECE0';

  return createTheme({
    palette: {
      mode,
      primary: { main: t.ember, light: t.amber, dark: t.ember, contrastText: t.onEmber },
      secondary: { main: t.amber, contrastText: t.onEmber },
      success: { main: isDark ? '#4ADE80' : '#3FA66A' },
      warning: { main: t.amber },
      error: { main: t.err },
      background: { default: t.bg, paper: t.bg2 },
      text: { primary: t.text, secondary: t.muted },
      divider: t.line,
    },
    shape: { borderRadius: 8 },
    typography: {
      fontFamily: FONT_BODY,
      h1: { fontFamily: FONT_DISPLAY, fontWeight: 800, letterSpacing: '-0.03em' },
      h2: { fontFamily: FONT_DISPLAY, fontWeight: 800, letterSpacing: '-0.025em' },
      h3: { fontFamily: FONT_DISPLAY, fontWeight: 800, letterSpacing: '-0.02em' },
      h4: { fontFamily: FONT_DISPLAY, fontWeight: 700, letterSpacing: '-0.02em' },
      h5: { fontFamily: FONT_DISPLAY, fontWeight: 700, letterSpacing: '-0.01em' },
      h6: { fontFamily: FONT_DISPLAY, fontWeight: 700 },
      subtitle1: { fontWeight: 600 },
      subtitle2: { fontWeight: 600 },
      button: { fontFamily: FONT_DISPLAY, fontWeight: 700 },
      overline: { fontFamily: FONT_MONO, letterSpacing: '0.14em', textTransform: 'uppercase', fontWeight: 700 },
    },
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          body: {
            backgroundColor: t.bg,
            backgroundImage: `radial-gradient(1100px 720px at 100% -8%, ${t.glow1}, transparent 60%), radial-gradient(900px 640px at -6% 108%, ${t.glow2}, transparent 58%)`,
            backgroundAttachment: 'fixed',
            minHeight: '100vh',
          },
        },
      },
      MuiAppBar: {
        // Masthead per the "Dragonsframe Home" design: ink in dark mode, deep blue in light.
        styleOverrides: {
          root: {
            backgroundImage: 'none',
            backgroundColor: isDark ? '#16110E' : '#26408B',
            color: isDark ? CREAM : '#FFFFFF',
            borderBottom: isDark ? '1px solid rgba(255,91,46,0.28)' : '1px solid rgba(255,255,255,0)',
            boxShadow: '0 1px 0 rgba(0,0,0,0.25)',
          },
        },
      },
      MuiButton: {
        styleOverrides: {
          root: { textTransform: 'none', borderRadius: 4 },
          containedPrimary: {
            backgroundImage: emberGradient(t),
            color: t.onEmber,
            boxShadow: `0 8px 22px -10px ${t.shadow}`,
            '&:hover': { backgroundImage: emberGradient(t), filter: 'brightness(1.06)', boxShadow: `0 14px 30px -12px ${t.shadow}` },
          },
          outlined: { borderColor: t.line },
        },
      },
      MuiPaper: {
        // Kill MUI's dark-mode elevation tint so paper matches our token exactly.
        styleOverrides: { root: { backgroundImage: 'none' } },
      },
      MuiCard: {
        styleOverrides: {
          root: {
            borderRadius: 10,
            border: `1px solid ${t.line}`,
            backgroundImage: 'none',
            boxShadow: isDark ? '0 12px 34px -18px rgba(0,0,0,0.6)' : '0 12px 34px -18px rgba(28,20,13,0.18)',
          },
        },
      },
      MuiChip: { styleOverrides: { root: { borderRadius: 999, fontWeight: 600 } } },
      MuiLink: { styleOverrides: { root: { color: t.ember, textDecorationColor: 'rgba(255,91,46,0.4)' } } },
      MuiOutlinedInput: { styleOverrides: { root: { borderRadius: 8 } } },
      MuiTab: { styleOverrides: { root: { textTransform: 'none', fontFamily: FONT_DISPLAY, fontWeight: 700 } } },
      MuiTooltip: { styleOverrides: { tooltip: { fontFamily: FONT_BODY } } },
    },
  });
}

// `inkMuted` retained for reference by masthead consumers if needed.
export const MASTHEAD = { ink: '#15110E', cream: '#F5ECE0', muted: 'rgba(245,236,224,0.62)' };
